import React, { useRef } from 'react';

import Alert from 'CommonComponents/Alert';
import Badge from 'CommonComponents/Badge';
import Button from 'CommonComponents/Button';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import CloudArrowUpIcon from 'Assets/icons/cloud-arrow-up.svg';
import FileDownloadIcon from 'Assets/icons/file-download.svg';

const ACCEPTED_TYPES = '.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Step 1. Choosing a file validates it straight away; nothing is written.
 */
export default function UploadChooseFile({ sampleUrl, templateVersion, validating, fileName, error, onFileChosen }) {
    const inputRef = useRef(null);

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

    const handleChange = (event) => {
        const file = event.target.files?.[0];
        // Cleared so choosing the same file again (after fixing it) still fires a change.
        event.target.value = '';

        if (file) {
            onFileChosen(file);
        }
    };

    return (
        <div className="tw-grid tw-grid-cols-1 tw-gap-4 lg:tw-grid-cols-3">
            <section className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-px-6 tw-py-6.5 lg:tw-col-span-2">
                <h3 className="table__header">{$t('Choose a file')}</h3>
                <p className="tw-mt-1 tw-text-sm tw-text-charcoal-75">
                    {$t('The file is checked as soon as you choose it. Nothing is written until you have seen what the upload will do and confirmed it.')}
                </p>

                <div className="tw-mt-6 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-3 tw-rounded-lg tw-border-2 tw-border-dashed tw-border-charcoal-25 tw-bg-charcoal-10 tw-px-6 tw-py-10 tw-text-center">
                    {validating ? (
                        <>
                            <LoadingSpinner />
                            <p className="tw-text-sm tw-text-charcoal-75">{$t('Checking {{file}}…', { file: fileName })}</p>
                        </>
                    ) : (
                        <>
                            <CloudArrowUpIcon className="tw-h-8 tw-w-8 tw-text-charcoal-50" aria-hidden="true" />
                            <p className="tw-text-sm tw-text-charcoal-75">{$t('Excel file (.xlsx or .xls) saved from the upload template')}</p>
                            <input ref={inputRef} type="file" accept={ACCEPTED_TYPES} className="tw-hidden" onChange={handleChange} id="actuals-upload-file" />
                            <Button variant="primary" onClick={() => inputRef.current?.click()}>
                                {$t('Choose file')}
                            </Button>
                        </>
                    )}
                </div>

                {!!error && (
                    <div className="tw-mt-4">
                        <Alert type="error">{error}</Alert>
                    </div>
                )}
            </section>

            <section className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-px-6 tw-py-6.5">
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-x-3">
                    <h3 className="table__header">{$t('Upload template')}</h3>
                    <Badge variant="info" size="sm">
                        {$t('Version {{version}}', { version: templateVersion })}
                    </Badge>
                </div>
                <p className="tw-mt-2 tw-text-sm tw-text-charcoal-75">
                    {$t(
                        'The template lists this budget’s departments, budget lines, GL codes, suppliers and contract references. Download a fresh copy whenever these change.',
                    )}
                </p>
                <form method="post" action={sampleUrl} className="tw-mt-4">
                    <input type="hidden" name="_token" value={csrfToken} />
                    <Button type="submit" variant="secondary" small>
                        <span className="tw-inline-flex tw-items-center tw-gap-x-1.5">
                            <FileDownloadIcon className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
                            {$t('Get sample file')}
                        </span>
                    </Button>
                </form>
                <ul className="tw-mt-4 tw-list-disc tw-space-y-1 tw-ps-5 tw-text-xs tw-text-charcoal-75">
                    <li>{$t('Rows are matched on Unique Document Reference: a new reference is added, a known one is updated in place.')}</li>
                    <li>{$t('Uploads never delete transactions.')}</li>
                    <li>{$t('A row without a budget line loads as unreconciled.')}</li>
                </ul>
            </section>
        </div>
    );
}
